/**
 * MiniProfileCarousel — horizontal-scroll list of InsightIQMiniProfile
 * entries. Reused 4× in the modal (creator-level lookalikes — admin only,
 * audience-level lookalikes, significant followers, significant likers).
 *
 * Each avatar links to the creator's IG / YT page in a new tab. Empty
 * when profiles array is empty; returns null.
 *
 * Image perf: native loading="lazy" + decoding="async" on every avatar
 * per spec §7.3. Scroll container uses scroll-snap for smooth thumb-scroll
 * on mobile.
 */
import type { InsightIQMiniProfile } from '@discovr/types';
import { useRef } from 'react';
import { SectionTitle } from '../charts/StatCard';
import { fmtNum } from '../utils/formatters';
import { CarouselArrows } from './CarouselArrows';

interface MiniProfileCarouselProps {
  label: string;
  subtitle?: string;
  profiles?: InsightIQMiniProfile[];
}

export function MiniProfileCarousel({ label, subtitle, profiles }: MiniProfileCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  if (!profiles || profiles.length === 0) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <SectionTitle>{label}</SectionTitle>
          {subtitle ? <div className="text-body-sm-regular text-[#737373]">{subtitle}</div> : null}
        </div>
        <CarouselArrows scrollRef={scrollRef} step={200} />
      </div>
      <div ref={scrollRef} className="-mx-1 mt-3 flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory">
        {profiles.map((p, i) => (
          <MiniProfileCard
            key={p.external_id || p.platform_username || p.url || `idx-${i}`}
            profile={p}
          />
        ))}
      </div>
    </div>
  );
}

function MiniProfileCard({ profile }: { profile: InsightIQMiniProfile }) {
  const followers = profile.follower_count ?? profile.subscriber_count;
  return (
    <a
      href={profile.url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`@${profile.platform_username ?? 'creator'} (${
        followers != null ? fmtNum(followers) + ' followers' : 'unknown followers'
      })`}
      className="group block shrink-0 w-20 text-center snap-start"
    >
      <div className="mx-auto size-12 overflow-hidden rounded-full border border-border">
        {profile.image_url ? (
          <img
            src={profile.image_url}
            alt=""
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
            className="size-full object-cover"
          />
        ) : (
          <div className="size-full bg-muted" />
        )}
      </div>
      <div className="mt-1 truncate text-[10px] font-medium text-foreground group-hover:text-foreground">
        @{profile.platform_username ?? '—'}
      </div>
      <div className="text-[10px] font-medium text-muted-foreground">
        {followers != null ? fmtNum(followers) : '—'}
        {profile.is_verified ? ' ✓' : ''}
      </div>
    </a>
  );
}
